import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { createSearchParams, useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import { MdCreate, MdDelete } from "react-icons/md";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";

import { ConfirmationPopup, DataGridTable, FormPage, GridAction, ToastType, notifyToast } from "../../components-old/ComponentsIndex";
import { DisplaySpinnerContext } from "../../context/ContextIndex";
import { selectSessionConfig } from "../../store/store-selector";
import { AppUtility } from "../../utils/utility";
import { handlePostAction } from "../../utils/event-api-invoke";
import { useFetchListData } from "../../hooks/useFetchListData";

const AuthorisedSignatureConfigListForm = () => {
  /**************************************************
  //Form handlers & hooks
  **************************************************/
  const { t } = useTranslation();
  const navigate = useNavigate();
  const spinnerCtx = useContext(DisplaySpinnerContext);
  const settingsConfig = useSelector(selectSessionConfig);

  /**************************************************
  //Form State variables
  **************************************************/
  const [authSignConfigRecordList, setAuthSignConfigRecordList] = useState([]);
  const [dataGridPageSize, setDataGridPageSize] = useState(10);
  const [gridPageSizeOptions] = useState([10, 20, 50]);
  const [totalAuthSignConfigRowCount, setTotalAuthSignConfigRowCount] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const AuthSignConfigList = useFetchListData(
    settingsConfig.businessCode,
    settingsConfig.countryCode,
    "getAuthSignConfig",
    "authorisedsignatureuserconfig"
  );

  /**************************************************
  //Event definitions
  **************************************************/
  //On click confirm Delete Confirmation Popup
  const handleConfirm = async () => {
    await handleDeleteAuthSignCostConfig(selectedRow);
    setPopupOpen(false);
    setSelectedRow(null);
  };

  //On click cancel Delete Confirmation Popup
  const handleCancel = () => {
    setSelectedRow(null);
    setPopupOpen(false);
  };

  //Craete Auth Sign Config button click
  const handleCreateAuthSignConfig = async () => {
    navigate("/authsignconfig");
  };

  //Data grid Page Size Change event
  const handlePageSizeChange = (newPageSize) => {
    setDataGridPageSize(newPageSize);
  };

  //On click Delete option each row
  const handleDeleteRow = (event) => {
    try {
      setSelectedRow(event);
      setPopupOpen(true);
    } catch (err) {
      alert(err);
    }
  };

  //On click Edit option each row
  const handleEditRow = async (param) => {
    try {
      let _navigationData = param.recordResponse;
      delete _navigationData.CreatedDate;
      delete _navigationData.ModifiedDate;
      delete _navigationData.CountryCode;
      delete _navigationData.BusinessCode;

      navigate(
        {
          pathname: "/authsignconfig",
          search: createSearchParams({
            authsignsonfigid: param.recordResponse.HashId,
          }).toString(),
        },
        { state: _navigationData }
      );
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //Fetch API & utility
  **************************************************/
  //Fetch API to Delete
  const handleDeleteAuthSignCostConfig = async (param) => {
    try {
      spinnerCtx.setLoadingIndicator(true);
      let _postRequest = {
        countryCode: settingsConfig.countryCode,
        businessCode: settingsConfig.businessCode,
        authorisedsignatureuserconfig: [
          {
            HashId: param.id,
            Deleted: true,
          },
        ],
      };

      let res = await handlePostAction(_postRequest, "authsignconfig");

      if (res?.status === 200) {
        if (res?.data?.response && res.data.response.errorNumber > 0) {
          notifyToast("Authorised Signature Config delete failed", ToastType.ERROR);
          spinnerCtx.setLoadingIndicator(false);
          return;
        }
        notifyToast("Authorised Signature Config deleted successfully", ToastType.SUCCESS);
        updateAuthSignConfigList(param.id);
      } else {
        notifyToast("Authorised Signature Config delete failed", ToastType.ERROR);
        spinnerCtx.setLoadingIndicator(false);
      }
    } catch (err) {
      alert(err);
    }
  };

  //Refesh Data Grid list after DELETE
  const updateAuthSignConfigList = (id) => {
    try {
      let _updatedList = [...authSignConfigRecordList];
      let _index = _updatedList.findIndex((item) => item.id === id);

      _updatedList.splice(_index, 1);

      const newTotalCount = totalAuthSignConfigRowCount - 1;
      const newPageSize = Math.min(newTotalCount, dataGridPageSize);
      setTotalAuthSignConfigRowCount(newTotalCount);
      setDataGridPageSize(newPageSize);
      setAuthSignConfigRecordList(_updatedList);
      spinnerCtx.setLoadingIndicator(false);
    } catch (err) {
      alert(err);
    }
  };

  /**************************************************
  //Form on load useEffect
  **************************************************/
  useEffect(() => {
    const setAuthSignConfigList = (recordlist) => {
      try {
        if (recordlist.length) recordlist.sort((a, b) => new Date(b.ModifiedDate).getTime() - new Date(a.ModifiedDate).getTime());
        let _recordList = recordlist.map((item) => {
          return {
            id: item.HashId,
            authSignConfigId: item.HashId,
            salesManager: item.SalesManager,
            salesUser: item.SalesUser,
            divisionalManager: item.DivisionalManager,
            pricingCommittee: item.PricingCommittee,
            createdBy: item.CreatedBy,
            modifiedBy: item.ModifiedBy,
            recordResponse: item,
          };
        });

        setAuthSignConfigRecordList(_recordList);
        setTotalAuthSignConfigRowCount(_recordList.length);
        spinnerCtx.setLoadingIndicator(false);
      } catch (err) {
        spinnerCtx.setLoadingIndicator(false);
        alert(err);
      }
    };

    spinnerCtx.setLoadingIndicator(true);
    if (AuthSignConfigList) {
      setAuthSignConfigList(AuthSignConfigList);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AuthSignConfigList]);

  /**************************************************
  //JSX delcaration
  **************************************************/
  const buttonConfigs = [
    {
      buttonText: <AddIcon />,
      buttonType: "filled",
      onClick: handleCreateAuthSignConfig,
      buttonColor: "#A62B23",
      textColor: "#fff",
      action: "create",
    },
    {
      buttonText: <SystemUpdateAltIcon />,
      buttonType: "outlined",
      onClick: () => {
        AppUtility.handleDownload(authSignConfigRecordList, "authorisedSignatureConfig.xlsx");
      },
      buttonColor: "#A62B23",
      textColor: "#A62B23",
      action: "download",
    },
  ];

  //action configs for edit and delete record
  const actionConfigs = [
    {
      icon: MdCreate,
      onClick: handleEditRow,
      buttonColor: "#EEEEE",
      action: "edit",
    },
    {
      icon: MdDelete,
      onClick: handleDeleteRow,
      buttonColor: "#A62B23",
      action: "delete",
    },
  ];

  const dataGridSchema = [
    {
      field: "salesUser",
      headerName: t("authSignConfigList.salesUser"),
      flex: 1,
    },
    {
      field: "salesManager",
      headerName: t("authSignConfigList.salesManager"),
      flex: 1,
    },
    {
      field: "divisionalManager",
      headerName: t("authSignConfigList.divisionalManager"),
      flex: 1,
    },
    {
      field: "pricingCommittee",
      headerName: t("authSignConfigList.pricingCommittee"),
      flex: 1,
    },
    {
      field: "action",
      headerName: t,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return <GridAction actionsConfig={actionConfigs} row={params.row} />;
      },
    },
  ];

  return (
    <FormPage isList={true}>
      <DataGridTable
        rows={authSignConfigRecordList}
        columns={dataGridSchema}
        pageSize={dataGridPageSize}
        pageSizeOptions={gridPageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
        totalRowCount={totalAuthSignConfigRowCount}
        toolbarHeader={t("authSignConfigList.header")}
        toolbarFilterPlaceholder={t("quoteApproval.toolbarFilterPlaceholder")}
        buttonConfigs={buttonConfigs}
      />
      <ConfirmationPopup
        isOpen={popupOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        confirmationText={t("popup.deleteAuthSignConfigConfirmationMessage")}
        eventObj={selectedRow}
      />
    </FormPage>
  );
};

export default AuthorisedSignatureConfigListForm;
